import React, {
  useState,
  ReactElement,
  useMemo,
  useCallback,
} from 'react';
import { Entity } from '../../types/entities/Entity';
import { Schema } from '../../types/Schema';
import { SchemaWithClient } from '../../types/SchemaWithClient';
import 'react-tabs/style/react-tabs.css';
import Dialog from '@mui/material/Dialog';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import DialogContent from '@mui/material/DialogContent';
import MinimizedContent from '../SidePane/MinimizedContent';
import Paper, { PaperProps } from '@mui/material/Paper';
import { v4 as uuidv4 } from 'uuid';
import Draggable from 'react-draggable';

type Props<T extends Entity> = {
  schema: Schema<T> | Schema<any>;
  id: number | undefined | string;
  onSubmit: (item: Partial<T>) => void;
  result?: (item: Partial<T>) => void;
  onClose: (item: any) => void;
  properties?: Partial<T>;
  propertiesFromTable: any;
  row?: any;
  hasSlidingPane?: boolean;
  demo?: boolean;
  addNewRow?: (row: any, tab: any, multiselect?: boolean) => void;
};

const countOpenDialogs = () => {
  return document.querySelectorAll('.MuiDialog-root').length;
};

const DEFAULT_PROPERTIES = {};
export const SlidingPaneEdit = <T extends Entity>({
  schema,
  onSubmit: handleSubmit,
  id,
  result,
  propertiesFromTable,
  row,
  onClose,
  hasSlidingPane,
  addNewRow,
  demo,
}: Props<T>): ReactElement => {
  const [open, setOpen] = useState(true);
  const [fullWidth, setFullWidth] = useState(false);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const draggablePaneId = useMemo(() => {
    return `draggable-responsive-add-new-dialog-${uuidv4()}`;
  }, []);

  const [position, setPosition] = useState<{ x: number; y: number }>({
    x: !countOpenDialogs() ? 0 : 35 * countOpenDialogs(),
    y: !countOpenDialogs() ? 0 : -20 * countOpenDialogs(),
  });

  const handleDrag = (e: any, data: any) => {
    setPosition({ x: data.x, y: data.y });
  };

  const PaperComponent = useCallback(
    (props: PaperProps) => {
      return (
        <Draggable
          handle={`#${draggablePaneId}`}
          cancel={'[class*="MuiDialogContent-root"], .non-draggable-button'}
          position={position}
          onStop={handleDrag}
        >
          <Paper {...props} />
        </Draggable>
      );
    },
    [position, draggablePaneId]
  );

  return (
    <>
      <Dialog
        PaperComponent={PaperComponent}
        id={draggablePaneId}
        fullScreen={fullScreen}
        open={open}
        onClose={(event) => {}}
        aria-labelledby="responsive-dialog-title"
        maxWidth={'md'}
        sx={{
          '& .MuiPaper-root': {
            width: {
              md: 'auto',
              lg: 'auto',
            },
            minWidth: {
              md: '600px',
              lg: '600px',
            },

            maxWidth: {
              md: 'calc(100vw - 400px)',
              lg: 'calc(100vw - 400px)',
            },
          },
          '& .MuiInputBase-root.MuiOutlinedInput-root': {
            borderRadius: '0px',
          },
         /* '& .MuiInputBase-root.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent',
          },*/
          '& .MuiInputBase-root.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#b3cdcae0',
          },
        }}
      >
        <DialogContent
          sx={{
            padding: '0px',
          }}
        >
          <MinimizedContent
            demo={demo}
            schema={schema}
            propertiesFromTable={propertiesFromTable}
            handleSubmit={(value: any) => {
              handleSubmit(value);
            }}
            onChangeFullWidth={(value) => {
              setFullWidth(value);
            }}
            hasSlidingPane={hasSlidingPane}
            onClose={(item: any) => {
              onClose(item);
            }}
            id={id}
            hasFullScreen={false}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

function schemaHasClient<T extends Entity>(
  schema: Schema<T>
): schema is SchemaWithClient<T> {
  return 'client' in schema;
}
